import { useState, useCallback } from 'react';
import {
  getMedia,
  uploadFile,
  uploadFromUrl,
  uploadDirectToCloudinary,
  deleteMedia,
  isValidImage,
  isValidFileSize,
  handleDrop as handleDropUtil,
  handlePaste as handlePasteUtil,
  handleDragOver as handleDragOverUtil
} from '../api/admin/media';

// Hook for media library management
export function useMediaLibrary(initialOptions = {}) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: initialOptions.limit || 20,
    total: 0,
    totalPages: 0
  });

  // Fetch media list
  const fetchMedia = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError(null);

      const result = await getMedia({
        page: options.page || pagination.page,
        limit: options.limit || pagination.limit
      });

      setMedia(result.media);
      setPagination(result.pagination);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  // Go to page
  const goToPage = useCallback((page) => {
    setPagination(prev => ({ ...prev, page }));
    fetchMedia({ page });
  }, [fetchMedia]);

  // Delete media item
  const removeMedia = useCallback(async (id) => {
    try {
      setLoading(true);
      await deleteMedia(id);
      setMedia(prev => prev.filter(m => m.id !== id));
      setPagination(prev => ({ ...prev, total: prev.total - 1 }));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Add uploaded media to list
  const addMedia = useCallback((newMedia) => {
    setMedia(prev => [newMedia, ...prev]);
    setPagination(prev => ({ ...prev, total: prev.total + 1 }));
  }, []);

  return {
    media,
    loading,
    error,
    pagination,
    fetchMedia,
    goToPage,
    removeMedia,
    addMedia,
    clearError: () => setError(null)
  };
}

// Hook for file uploads
export function useMediaUpload(options = {}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [uploadedMedia, setUploadedMedia] = useState(null);

  const { onSuccess, onError, maxSizeMB = 10, useDirect = false } = options;

  // Upload a single file
  const upload = useCallback(async (file, uploadOptions = {}) => {
    // Validate
    if (!isValidImage(file)) {
      const err = new Error('Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP, SVG).');
      setError(err.message);
      onError?.(err);
      throw err;
    }

    if (!isValidFileSize(file, maxSizeMB)) {
      const err = new Error(`File too large. Maximum size is ${maxSizeMB}MB.`);
      setError(err.message);
      onError?.(err);
      throw err;
    }

    try {
      setUploading(true);
      setProgress(10);
      setError(null);

      let result;
      if (useDirect) {
        // Direct upload to Cloudinary (better for large files)
        setProgress(30);
        result = await uploadDirectToCloudinary(file, uploadOptions);
      } else {
        // Upload through our server
        setProgress(30);
        result = await uploadFile(file, uploadOptions);
      }

      setProgress(100);
      setUploadedMedia(result.media || result);
      onSuccess?.(result.media || result);
      return result;
    } catch (err) {
      setError(err.message);
      onError?.(err);
      throw err;
    } finally {
      setUploading(false);
      // Reset progress after a delay
      setTimeout(() => setProgress(0), 1000);
    }
  }, [maxSizeMB, useDirect, onSuccess, onError]);

  // Upload from URL
  const uploadUrl = useCallback(async (url, uploadOptions = {}) => {
    try {
      setUploading(true);
      setProgress(30);
      setError(null);

      const result = await uploadFromUrl(url, uploadOptions);

      setProgress(100);
      setUploadedMedia(result.media);
      onSuccess?.(result.media);
      return result;
    } catch (err) {
      setError(err.message);
      onError?.(err);
      throw err;
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  }, [onSuccess, onError]);

  // Upload multiple files
  const uploadMultiple = useCallback(async (files, uploadOptions = {}) => {
    const results = [];
    const errors = [];

    for (let i = 0; i < files.length; i++) {
      try {
        const result = await upload(files[i], uploadOptions);
        results.push(result);
      } catch (err) {
        errors.push({ file: files[i].name, error: err.message });
      }
    }

    return { results, errors };
  }, [upload]);

  return {
    upload,
    uploadUrl,
    uploadMultiple,
    uploading,
    progress,
    error,
    uploadedMedia,
    clearError: () => setError(null),
    clearUploaded: () => setUploadedMedia(null)
  };
}

// Hook for drag and drop
export function useMediaDragDrop(options = {}) {
  const [isDragging, setIsDragging] = useState(false);
  const { onDrop, onError } = options;

  const handleDragOver = useCallback((e) => {
    handleDragOverUtil(e);
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    setIsDragging(false);
    const files = handleDropUtil(e);

    if (files.length === 0) {
      onError?.(new Error('No valid images found'));
      return [];
    }

    onDrop?.(files);
    return files;
  }, [onDrop, onError]);

  const handlePaste = useCallback((e) => {
    const files = handlePasteUtil(e);
    if (files.length > 0) {
      onDrop?.(files);
    }
    return files;
  }, [onDrop]);

  return {
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    dropZoneProps: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop
    }
  };
}

// Combined hook for full media upload experience
export function useMediaUploader(options = {}) {
  const upload = useMediaUpload(options);
  const dragDrop = useMediaDragDrop({
    onDrop: (files) => {
      if (files.length === 1) {
        upload.upload(files[0]);
      } else if (files.length > 1) {
        upload.uploadMultiple(files);
      }
    },
    onError: options.onError
  });

  return {
    ...upload,
    ...dragDrop
  };
}

export default useMediaLibrary;
